import features from 'config/featureFlags';
import { Header } from 'components/Header';
import { useGetCategoriesPh } from 'services/categoriesPh/queries/useGetCategoriesPh';
import { getSearchPreview } from 'services/products';
import { searchEventGTM, selectContentGTM } from 'shared/gtm';
import usePosSearch from 'shared/pos/usePosSearch';
import useBasket from 'stores/basket';
import useCatalog from 'stores/catalog';
import useCustomer from 'stores/customer';
import usePos from 'stores/pos';
import { NavigationHeaderProps } from './interfaces';
import { useMemo } from 'react';
import { HeaderProps } from 'components/Header/interfaces';
import { useLocation, useNavigate } from 'react-router-dom';
import useCustomerMenu from './hooks/useCustomerMenu';
import useFidelityMenu from './hooks/useFidelityMenu';
import useLogoMenu from './hooks/useLogoMenu';
import { openForbiddenModal } from 'shared/ForbiddenActionModal';
import { useZendesk } from 'routes/hooks/useZendesk';

export default function NavigationHeader({
	headerType,
	stickyTopRow,
	showHelpButton,
	isGlobalBoundary,
	landingPageHeader,
}: NavigationHeaderProps) {
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const { catalogStore } = useCatalog();
	const { basketStore, handleOpenBasket } = useBasket();
	const { posStore, handleSelectPos, selectedPos } = usePos();
	const {
		isPartner,
		mainCustomerPage,
		simulatedViewType,
		getRestrictionType,
		customerStore,
		customerRestrictionRedirect,
	} = useCustomer();

	const { closeZendesk } = useZendesk();

	const { data: customer, partnerLabel } = customerStore;
	const { catalogMenuItems, incentiveCampaignFlags } = catalogStore;
	const { basket, loading: loadingBasket } = basketStore;

	const pos = posStore.selectedPos;
	const customerRestriction = getRestrictionType();
	const volativeHeaderType =
		headerType === 'logged_and_not_logged' && customer && customerRestriction !== '2FA_EXPIRED'
			? 'logged'
			: 'not_logged';

	const type = useMemo(() => {
		if (isGlobalBoundary) return 'logged_and_not_logged';

		if (landingPageHeader) return landingPageHeader;

		return headerType === 'logged_and_not_logged' ? volativeHeaderType : headerType;
	}, [isGlobalBoundary, volativeHeaderType, headerType, landingPageHeader]);

	const handleNavigate = {
		logout: () => navigate('/login', { state: { logout: true }, replace: true }),
		refresh: () => navigate(0),
	};

	const orderImportControls = useMemo((): HeaderProps['orderImportControls'] => {
		const hiddenBy = {
			twoFAFlow: pathname.includes('verificacao-de-identidade'),
			noCustomerToken: !customerStore.token,
			deniedCustomerRestriction: ['QUOTATION', 'NO_LINKS'].includes(customerRestriction || ''),
		};

		if (Object.values(hiddenBy).some(Boolean)) return { visible: false };

		const defaultControls = {
			visible: true,
			onClick: () => navigate('/importacao-pedido'),
		};

		if (isPartner() && !features.VITE_PARTNER_ORDER_IMPORT) {
			defaultControls.onClick = openForbiddenModal;
		}

		return defaultControls;
	}, [customerRestriction, customerStore.token, pathname, isPartner]);

	const alternativeNavigationBanner = useMemo((): HeaderProps['alternativeNavigationBanner'] => {
		if (isPartner() && partnerLabel)
			return {
				showMore: true,
				title: `Jornada Exclusiva ${partnerLabel}`,
				description: 'Algumas funcionalidades não estarão disponíveis neste acesso.',
			};

		if (customerRestriction === 'SIMULATED')
			return {
				icon: 'lock',
				title: 'Ambiente simulado:',
				description: 'Essa é apenas uma cópia da navegação da farmácia.',
			};

		return undefined;
	}, [customerRestriction, partnerLabel, isPartner]);

	const logoMenuProps = useLogoMenu({
		pathname,
		customerId: customer?.id,
		defaultRedirect: mainCustomerPage(),
		isGlobalBoundary,
		selectedPosId: pos.id,
		customerRestriction,
		customerRestrictionRedirect,
		logout: handleNavigate.logout,
		refresh: handleNavigate.refresh,
	});
	const fidelityMenuProps = useFidelityMenu({ customerRestriction, openForbiddenModal });
	const customerMenuProps = useCustomerMenu({
		headerType: type,
		hyperaRole: customer?.hyperaRole,
		customerName: customer?.name,
		selectedPosId: pos?.id,
		showHelpButton,
		customerPosRole: pos?.role,
		customerRestriction,
		orderImportControls,
		logout: handleNavigate.logout,
		incentiveCampaignFlags,
	});

	const hookProps = {
		...logoMenuProps,
		...fidelityMenuProps,
		...customerMenuProps,
	};

	return (
		<Header
			useGetCategoriesPh={useGetCategoriesPh}
			orderImportControls={orderImportControls}
			basketData={basket}
			catalogMenuItems={catalogMenuItems}
			customerStore={customerStore}
			getSearchPreview={getSearchPreview}
			getUsePosSearch={usePosSearch}
			handleOpenBasket={handleOpenBasket}
			handleSelectPos={handleSelectPos}
			isSuggestionsProductNotifierExpired
			loadingBasket={loadingBasket}
			loadingPos={posStore.loading}
			mainCustomerPage={mainCustomerPage}
			posStore={posStore}
			searchEventGTM={searchEventGTM}
			selectContentGTM={selectContentGTM}
			selectedPos={selectedPos}
			simulatedViewType={simulatedViewType}
			stickyTopRow={stickyTopRow}
			type={type}
			alternativeNavigationBanner={alternativeNavigationBanner}
			onOpenChangeDroplistProfileMenu={() => closeZendesk()}
			{...hookProps}
		/>
	);
}
